import React, { createContext, useContext, useState, useEffect } from 'react';
import { workflowAPI } from '../services/api';
import { extractRevisedContent } from '../utils/contentParser';

const WorkflowContext = createContext();

export const useWorkflow = () => {
  const context = useContext(WorkflowContext);
  if (!context) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};

export const WorkflowProvider = ({ children }) => {
  // Session management
  const [sessionId, setSessionId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Content states
  const [storyContent, setStoryContent] = useState(null);
  const [dialoguesContent, setDialoguesContent] = useState(null);
  const [questionsContent, setQuestionsContent] = useState(null);
  
  // UI states
  const [currentStage, setCurrentStage] = useState('story');
  const [status, setStatus] = useState('idle');
  const [completedStages, setCompletedStages] = useState(new Set());
  
  // Feedback states
  const [feedbackText, setFeedbackText] = useState('');
  const [isRequestingChanges, setRequestingChanges] = useState(false);
  const [isApproving, setApproving] = useState(false);
  
  // Initialize session
  useEffect(() => {
    const savedSessionId = localStorage.getItem('workflowSessionId');
    if (savedSessionId) {
      setSessionId(savedSessionId);
      setCurrentStage('story');
      setStatus('ready');
      // Load existing content
      loadStageContent(savedSessionId);
    }
  }, []);
  
  // Load stage content from API
  const loadStageContent = async (sessionId, preserveExisting = false) => {
    try {
      const response = await workflowAPI.getCurrentStage(sessionId);
      
      // Update content based on API response
      // The API returns data in response.data object
      const content = response.data || response;
      
      if (content.story) {
        const revisedStory = extractRevisedContent(content.story, 'story');
        
        // Only update if we have revised content or if not preserving existing
        if (revisedStory !== content.story || !preserveExisting) {
          setStoryContent(revisedStory);
        } else {
        }
      } else {
      }
      
      if (content.dialogues) {
        const revisedDialogues = extractRevisedContent(content.dialogues, 'dialogues');
        
        // Only update if we have revised content or if not preserving existing
        if (revisedDialogues !== content.dialogues || !preserveExisting) {
          setDialoguesContent(revisedDialogues);
        } else {
        }
      }
      
      if (content.questions) {
        const revisedQuestions = extractRevisedContent(content.questions, 'questions');
        
        // Only update if we have revised content or if not preserving existing
        if (revisedQuestions !== content.questions || !preserveExisting) {
          setQuestionsContent(revisedQuestions);
        } else {
        }
      }
      
      if (response.current_stage) {
        setCurrentStage(response.current_stage);
      }
      
      if (response.completed_stages) {
        setCompletedStages(new Set(response.completed_stages));
      }
      
    } catch (error) {
      console.error('Failed to load stage content:', error);
      // Don't throw error here, just log it
    }
  };

  // Start new workflow session
  const startWorkflow = async (data) => {
    try {
      setIsProcessing(true);
      setStatus('processing');
      
      
      // Call the real API
      const response = await workflowAPI.startWorkflow(data);
      
      const newSessionId = response.session_id;
      setSessionId(newSessionId);
      localStorage.setItem('workflowSessionId', newSessionId);
      
      // Load initial content
      await loadStageContent(newSessionId);
      
      setStatus('ready');
      setCurrentStage('story');
      setCompletedStages(new Set());
      
      return newSessionId;
    } catch (error) {
      console.error('Failed to start workflow:', error);
      setStatus('error');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Approve current stage
  const approveStage = async (sessionId) => {
    try {
      setIsProcessing(true);
      setApproving(true);
      
      
      // Call the real API
      const response = await workflowAPI.approveStage(sessionId, true);
      
      // Reload content to get updated state
      await loadStageContent(sessionId);
      
      setStatus('ready');
    } catch (error) {
      console.error('Failed to approve stage:', error);
      throw error;
    } finally {
      setIsProcessing(false);
      setApproving(false);
    }
  };
  
  // Request changes for current stage
  const requestChanges = async (sessionId, stage, feedback) => {
    
    try {
      setIsProcessing(true);
      
      // Store current content to preserve it
      const currentStoryContent = storyContent;
      const currentDialoguesContent = dialoguesContent;
      const currentQuestionsContent = questionsContent;
      
      
      // Clear only the current stage content to prevent duplicates
      if (stage === 'dialogues') {
        setDialoguesContent(null);
      } else if (stage === 'story') {
        setStoryContent(null);
      } else if (stage === 'questions') {
        setQuestionsContent(null);
      }
      
      
      // Test server connectivity first
      try {
        const healthCheck = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/health`, {
          method: 'GET',
          timeout: 5000
        });
      } catch (healthError) {
        console.warn('Server health check failed:', healthError.message);
      }
      
      // Extract target section from feedback
      const extractTargetSection = (feedback) => {
        const feedbackLower = feedback.toLowerCase();
        
        if (feedbackLower.includes('summary') || feedbackLower.includes('story')) {
          return 'summary';
        } else if (feedbackLower.includes('learning outcome') || feedbackLower.includes('outcome')) {
          return 'learning_outcomes';
        } else if (feedbackLower.includes('keyword') || feedbackLower.includes('term')) {
          return 'keywords';
        } else if (feedbackLower.includes('concept')) {
          return 'concepts';
        }
        
        return 'summary'; // Default to summary if unclear
      };
      
      const targetSection = extractTargetSection(feedback);
      
      // Call the real API with simple retry mechanism
      const startTime = Date.now();
      
      let response;
      let lastError;
      
      // Try up to 3 times with simple logic
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          
          // Use appropriate API call based on stage
          if (stage === 'story') {
            // For story, use section-specific API call
            response = await Promise.race([
              workflowAPI.processSectionChanges(sessionId, stage, targetSection, feedback, currentStoryContent),
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Request timeout after 30 seconds')), 30000)
              )
            ]);
          } else {
            // For dialogues and questions, use general API call
            const existingContent = stage === 'dialogues' ? currentDialoguesContent : 
                                  stage === 'questions' ? currentQuestionsContent : null;
            
            response = await Promise.race([
              workflowAPI.processChanges(sessionId, stage, feedback, existingContent),
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Request timeout after 30 seconds')), 30000)
              )
            ]);
          }
          
          break; // Success, exit retry loop
          
        } catch (apiError) {
          lastError = apiError;
          console.error(`API call attempt ${attempt} failed:`, apiError.message);
          
          if (attempt < 3) {
            await new Promise(resolve => setTimeout(resolve, 3000));
          }
        }
      }
      
      if (!response) {
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        console.error('All API call attempts failed');
        
        throw lastError;
      }
      
      const endTime = Date.now();
      const duration = endTime - startTime;
      
      
      // Reload content to get updated content, but preserve existing content
      await loadStageContentWithPreservation(sessionId, {
        story: currentStoryContent,
        dialogues: currentDialoguesContent,
        questions: currentQuestionsContent
      }, targetSection);
      
      setStatus('ready');
    } catch (error) {
      console.error('Context: Failed to request changes:', error);
      // Re-throw the error so the calling component can handle it
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Merge story content sections to preserve previously revised sections
  const mergeStoryContentSections = (existingContent, newContent) => {
    
    const sections = ['Summary', 'Learning Outcomes', 'Keywords', 'Concepts'];
    let mergedContent = newContent;
    
    // For each section, check if existing content has a revised version
    for (const section of sections) {
      const existingSection = extractSectionContent(existingContent, section);
      const newSection = extractSectionContent(newContent, section);
      
      
      // If existing content has this section and it's different from new content,
      // and the existing section appears to be revised (contains "REVISED" or is longer)
      if (existingSection && newSection && 
          existingSection !== newSection && 
          (existingSection.includes('REVISED') || existingSection.length > newSection.length)) {
        
        
        // Use more precise replacement to avoid mixing sections
        const sectionPatterns = {
          'Summary': /(?:Summary|Story)[\s\S]*?(?=Learning Outcomes|Keywords|Concepts|$)/i,
          'Learning Outcomes': /Learning Outcomes[\s\S]*?(?=Summary|Keywords|Concepts|$)/i,
          'Keywords': /Keywords[\s\S]*?(?=Summary|Learning Outcomes|Concepts|$)/i,
          'Concepts': /Concepts[\s\S]*?(?=Summary|Learning Outcomes|Keywords|$)/i
        };
        
        const pattern = sectionPatterns[section];
        if (pattern) {
          mergedContent = mergedContent.replace(pattern, existingSection);
        }
      }
    }
    
    return mergedContent;
  };
  
  // Extract a specific section from content
  const extractSectionContent = (content, sectionName) => {
    // More precise pattern to extract sections
    const sectionPatterns = {
      'Summary': /(?:Summary|Story)[\s\S]*?(?=Learning Outcomes|Keywords|Concepts|$)/i,
      'Learning Outcomes': /Learning Outcomes[\s\S]*?(?=Summary|Keywords|Concepts|$)/i,
      'Keywords': /Keywords[\s\S]*?(?=Summary|Learning Outcomes|Concepts|$)/i,
      'Concepts': /Concepts[\s\S]*?(?=Summary|Learning Outcomes|Keywords|$)/i
    };
    
    const pattern = sectionPatterns[sectionName];
    if (!pattern) return null;
    
    const match = content.match(pattern);
    return match ? match[0].trim() : null;
  };

  // Intelligent section merging: smart handling of server responses
  const intelligentSectionMerge = (existingContent, newContent, targetSection) => {
    
    // Map target section names
    const sectionMapping = {
      'summary': 'Summary',
      'learning_outcomes': 'Learning Outcomes',
      'keywords': 'Keywords',
      'concepts': 'Concepts'
    };
    
    const targetSectionName = sectionMapping[targetSection] || 'Summary';
    
    // Extract all sections from both existing and new content
    const existingSections = extractAllSections(existingContent);
    const newSections = extractAllSections(newContent);
    
    
    // Start with existing content as base
    let mergedContent = existingContent;
    
    // For the target section, use the revised version from new content
    if (newSections[targetSectionName]) {
      mergedContent = replaceSection(mergedContent, targetSectionName, newSections[targetSectionName]);
    }
    
    // For other sections, check if they have been revised in new content
    // If they have, use the revised version; otherwise keep existing
    const otherSections = ['Summary', 'Learning Outcomes', 'Keywords', 'Concepts'].filter(s => s !== targetSectionName);
    
    for (const section of otherSections) {
      if (newSections[section]) {
        // Check if this section appears to be revised (contains "REVISED" or is significantly different)
        const isRevised = newSections[section].includes('REVISED') || 
                         newSections[section].length > (existingSections[section]?.length || 0) * 1.2;
        
        if (isRevised) {
          mergedContent = replaceSection(mergedContent, section, newSections[section]);
        } else {
        }
      }
    }
    
    
    return mergedContent;
  };

  // Extract all sections from content using the same logic as StoryStage
  const extractAllSections = (content) => {
    
    if (!content) return {};
    
    const sections = {};
    const lines = content.split('\n');
    let currentSection = null;
    let currentContent = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check if this line is a section header (must be short and not numbered)
      if ((line.toLowerCase() === 'story:' || line === 'Story:') && 
          line.length < 20 && !/^\d+\./.test(line)) {
        // Save previous section
        if (currentSection && currentContent.length > 0) {
          sections[currentSection] = currentContent.join('\n').trim();
        }
        currentSection = 'Summary';
        currentContent = [];
      } else if ((line.toLowerCase() === 'learning outcomes:' || line === 'Learning Outcomes:' || 
                 line.toLowerCase() === 'learning outcomes.' || line === 'Learning Outcomes.' ||
                 line.toLowerCase() === 'learning outcomes' || line === 'Learning Outcomes') && 
                 line.length < 30 && !/^\d+\./.test(line)) {
        // Save previous section
        if (currentSection && currentContent.length > 0) {
          sections[currentSection] = currentContent.join('\n').trim();
        }
        currentSection = 'Learning Outcomes';
        currentContent = [];
      } else if ((line.toLowerCase() === 'keywords:' || line === 'Keywords:') && 
                 line.length < 20 && !/^\d+\./.test(line)) {
        // Save previous section
        if (currentSection && currentContent.length > 0) {
          sections[currentSection] = currentContent.join('\n').trim();
        }
        currentSection = 'Keywords';
        currentContent = [];
      } else if ((line.toLowerCase() === 'concepts:' || line === 'Concepts:') && 
                 line.length < 20 && !/^\d+\./.test(line)) {
        // Save previous section
        if (currentSection && currentContent.length > 0) {
          sections[currentSection] = currentContent.join('\n').trim();
        }
        currentSection = 'Concepts';
        currentContent = [];
      } else if (currentSection && line && !line.startsWith('&')) {
        // Add content to current section (skip empty lines and & markers)
        currentContent.push(line);
      }
    }
    
    // Save the last section
    if (currentSection && currentContent.length > 0) {
      sections[currentSection] = currentContent.join('\n').trim();
    }
    
    return sections;
  };

  // Replace a specific section in content
  const replaceSection = (content, sectionName, newSectionContent) => {
    const sectionPatterns = {
      'Summary': /(?:Summary|Story)[\s\S]*?(?=Learning Outcomes|Keywords|Concepts|$)/i,
      'Learning Outcomes': /Learning Outcomes[\s\S]*?(?=Summary|Keywords|Concepts|$)/i,
      'Keywords': /Keywords[\s\S]*?(?=Summary|Learning Outcomes|Concepts|$)/i,
      'Concepts': /Concepts[\s\S]*?(?=Summary|Learning Outcomes|Keywords|$)/i
    };
    
    const pattern = sectionPatterns[sectionName];
    if (pattern) {
      return content.replace(pattern, newSectionContent);
    }
    
    return content;
  };

  // Load stage content with preservation of existing revised content
  const loadStageContentWithPreservation = async (sessionId, existingContent, targetSection = 'summary') => {
    try {
      const response = await workflowAPI.getCurrentStage(sessionId);
      
      const content = response.data || response;
      
      // Update story content
      if (content.story) {
        
        const revisedStory = extractRevisedContent(content.story, 'story');
        
        // Check for learning outcomes in both
        const serverOutcomes = content.story.match(/learning\s+outcomes?\s*:?\s*([\s\S]*?)(?=\n\n|\n[A-Z]|$)/i);
        const revisedOutcomes = revisedStory.match(/learning\s+outcomes?\s*:?\s*([\s\S]*?)(?=\n\n|\n[A-Z]|$)/i);
        
        
        
        // Use revised content directly for now (disable intelligent merging temporarily)
        let finalStoryContent = revisedStory;
        
        if (existingContent.story) {
        }
        
        setStoryContent(finalStoryContent);
      } else if (existingContent.story) {
        setStoryContent(existingContent.story);
      }
      
      // Update dialogues content
      if (content.dialogues) {
        const revisedDialogues = extractRevisedContent(content.dialogues, 'dialogues');
        
        // Only update if we have new revised content or no existing content
        if (revisedDialogues !== content.dialogues || !existingContent.dialogues) {
          setDialoguesContent(revisedDialogues);
        } else {
          setDialoguesContent(existingContent.dialogues);
        }
      } else if (existingContent.dialogues) {
        setDialoguesContent(existingContent.dialogues);
      }
      
      // Update questions content
      if (content.questions) {
        const revisedQuestions = extractRevisedContent(content.questions, 'questions');
        
        // Only update if we have new revised content or no existing content
        if (revisedQuestions !== content.questions || !existingContent.questions) {
          setQuestionsContent(revisedQuestions);
        } else {
          setQuestionsContent(existingContent.questions);
        }
      } else if (existingContent.questions) {
        setQuestionsContent(existingContent.questions);
      }
      
      if (response.current_stage) {
        setCurrentStage(response.current_stage);
      }
      
      if (response.completed_stages) {
        setCompletedStages(new Set(response.completed_stages));
      }
      
    } catch (error) {
      console.error('Failed to load stage content with preservation:', error);
      throw error;
    }
  };

  // Reload content
  const reloadContent = async () => {
    try {
      setIsProcessing(true);
      
      if (sessionId) {
        
        // Store current content to preserve it
        const currentContent = {
          story: storyContent,
          dialogues: dialoguesContent,
          questions: questionsContent
        };
        
        // Use preservation logic to maintain revised content
        await loadStageContentWithPreservation(sessionId, currentContent);
      }
      
      setStatus('ready');
    } catch (error) {
      console.error('Failed to reload content:', error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Generate sample content (fallback when API is not available)
  const generateSampleContent = (stage) => {
    switch (stage) {
      case 'story':
        return `# Educational Story: Learning Through Adventure

## Chapter 1: The Beginning
Once upon a time, in a magical classroom far away, students discovered the power of learning through interactive storytelling.

## Chapter 2: The Journey
The students embarked on an educational adventure, exploring new concepts and ideas through engaging narratives.

## Chapter 3: The Discovery
Through their journey, they learned that education can be both fun and effective when presented in the right way.

## Conclusion
This story demonstrates how educational content can be made more engaging through storytelling techniques.`;
      
      case 'dialogues':
        return `# Educational Dialogues

## Dialogue 1: Introduction
**Teacher:** Welcome to our interactive learning session! Today we'll explore new concepts together.
**Student:** I'm excited to learn! What will we be studying today?
**Teacher:** We'll be diving into the fascinating world of educational technology and its impact on learning.

## Dialogue 2: Concept Exploration
**Teacher:** Let's start with the basics. What do you think educational technology means?
**Student:** I think it's about using computers and technology to help us learn better.
**Teacher:** That's a great start! Educational technology encompasses any tool or method that enhances the learning experience.

## Dialogue 3: Application
**Student:** How can we apply these concepts in our daily learning?
**Teacher:** Excellent question! We can use interactive tools, multimedia content, and collaborative platforms to make learning more engaging and effective.`;
      
      case 'questions':
        return `# Assessment Questions

## Question 1
What is the main purpose of educational technology?
A) To replace traditional teaching methods
B) To enhance and support the learning process
C) To make learning easier without effort
D) To reduce the need for teachers

**Answer:** B
**Explanation:** Educational technology is designed to enhance and support learning, not replace teachers or make learning effortless.

## Question 2
Which of the following is NOT a benefit of interactive learning?
A) Increased student engagement
B) Better retention of information
C) Reduced need for critical thinking
D) Improved collaboration skills

**Answer:** C
**Explanation:** Interactive learning actually promotes critical thinking rather than reducing it.

## Question 3
What role do teachers play in technology-enhanced learning?
A) They become obsolete
B) They guide and facilitate the learning process
C) They only need to know how to use computers
D) They should avoid using technology

**Answer:** B
**Explanation:** Teachers remain essential as guides and facilitators, helping students navigate and make the most of educational technology.`;
      
      default:
        return 'No content available for this stage.';
    }
  };
  
  const value = {
    // Session
    sessionId,
    isProcessing,
    startWorkflow,
    
    // Content
    storyContent,
    dialoguesContent,
    questionsContent,
    
    // UI
    currentStage,
    status,
    completedStages,
    
    // Actions
    approveStage,
    requestChanges,
    reloadContent,
    
    // Feedback
    feedbackText,
    setFeedbackText,
    isRequestingChanges,
    setRequestingChanges,
    isApproving,
    setApproving,
  };
  
  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  );
};